import React, { useState, useEffect } from 'react';
import { 
  BuildingOfficeIcon, 
  PlusIcon, 
  MapPinIcon, 
  UserIcon, 
  PhoneIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../utils/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

interface Location {
  id: string;
  name: string;
  address?: string;
  description?: string;
  contact_person?: string;
  contact_phone?: string;
  is_active: boolean;
  pond_count: number;
  business_types?: string[];
  created_at: string;
}

interface LocationFormData {
  name: string;
  address?: string;
  description?: string;
  contact_person?: string;
  contact_phone?: string;
  business_types?: string[];
}

interface Pond {
  id: string;
  name: string;
  pond_type: 'nursery' | 'grower';
  location_id: string;
  size_sqm?: number;
  created_at: string;
}

interface PondFormData {
  name: string;
  pond_type: 'nursery' | 'grower';
  size_sqm: number;
}

const locationSchema = yup.object({
  name: yup.string().required('Location name is required'),
  address: yup.string().optional(),
  description: yup.string().optional(),
  contact_person: yup.string().optional(),
  contact_phone: yup.string().optional(),
  business_types: yup.array().of(yup.string()).min(1, 'Select at least one business type').required('Business type is required'),
});

const pondSchema = yup.object({
  name: yup.string().required('Pond name is required'),
  pond_type: yup.string().oneOf(['nursery', 'grower']).required('Pond type is required'),
  size_sqm: yup.number().positive('Pond area must be greater than 0').required('Pond area is required'),
});

const DEMO_LOCATION_ID = 'demo-location';

const LocationManagement: React.FC = () => {
  const { user } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [error, setError] = useState('');
  
  // Pond management state
  const [showPondManagement, setShowPondManagement] = useState(false);
  const [selectedLocationForPonds, setSelectedLocationForPonds] = useState<Location | null>(null);
  const [ponds, setPonds] = useState<Pond[]>([]);
  const [showPondForm, setShowPondForm] = useState(false);
  const [editingPond, setEditingPond] = useState<Pond | null>(null);

  // Use the useApi hook for loading locations
  const { data: locations, loading, error: loadError, refetch } = useApi<Location[]>('/locations');
  const isFreePlan = (user?.plan || 'free') === 'free';
  const locationCount = locations?.length || 0;
  const freeLocationLimitReached = isFreePlan && locationCount >= 1;
  
  const { mutate: createLocation, loading: creating } = useMutation<Location, LocationFormData>();
  const { mutate: updateLocation } = useMutation<Location, LocationFormData>();
  const { mutate: deleteLocation } = useMutation<any, any>();
  
  // Pond mutations
  const { mutate: createPond, loading: creatingPond } = useMutation<Pond, PondFormData & { location_id: string }>();
  const { mutate: updatePond } = useMutation<Pond, PondFormData & { pond_id: string }>();
  const { mutate: deletePond } = useMutation<any, any>();
  
  const form = useForm<LocationFormData>({
    resolver: yupResolver(locationSchema) as any,
    defaultValues: {
      name: '',
      address: '',
      description: '',
      contact_person: '',
      contact_phone: '',
      business_types: ['fish'],
    }
  });

  const pondForm = useForm<PondFormData>({
    resolver: yupResolver(pondSchema) as any,
    defaultValues: {
      name: '',
      pond_type: 'nursery',
      size_sqm: 0,
    }
  });

  // Use loadError from useApi hook
  useEffect(() => {
    if (loadError) {
      setError(loadError);
    }
  }, [loadError]);

  const handleCreateLocation = async (data: LocationFormData) => {
    if (freeLocationLimitReached) {
      setError('Free plan allows only 1 farm. Upgrade to PRO to add more farms.');
      setShowCreateForm(false);
      return;
    }
    try {
      const newLocation = await createLocation('/locations', data);
      if (newLocation) {
        setShowCreateForm(false);
        form.reset();
        // Refetch locations to update the list
        refetch();
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEditLocation = (location: Location) => {
    setEditingLocation(location);
    form.reset({
      name: location.name,
      address: location.address || '',
      description: location.description || '',
      contact_person: location.contact_person || '',
      contact_phone: location.contact_phone || '',
      business_types: location.business_types || ['fish'],
    });
    setShowCreateForm(true);
  };

  const handleUpdateLocation = async (data: LocationFormData) => {
    if (!editingLocation) return;
    
    try {
      await updateLocation(`/locations/${editingLocation.id}`, data, 'PUT');
      setShowCreateForm(false);
      setEditingLocation(null);
      form.reset();
      // Refetch locations to update the list
      refetch();
    } catch (err: any) {
      setError(err.message || 'Failed to update location');
    }
  };

  const handleDeleteLocation = async (locationId: string) => {
    if (locationId === DEMO_LOCATION_ID) {
      setError('Demo location cannot be deleted.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this location?')) {
      return;
    }

    try {
      await deleteLocation(`/locations/${locationId}`, {}, 'DELETE');
      // Refetch locations to update the list
      refetch();
    } catch (err: any) {
      setError(err.message || 'Failed to delete location');
    }
  };

  // Pond management functions
  const handleManagePonds = async (location: Location) => {
    console.log('Manage Ponds clicked for location:', location.name);
    setSelectedLocationForPonds(location);
    setShowPondManagement(true);
    setPonds([]);
    
    try {
      // Load ponds for this location using the API client
      const response = await apiClient.get(`/locations/${location.id}/ponds`);
      setPonds(response.data);
    } catch (err: any) {
      console.error('Failed to load ponds:', err);
      setError(err.response?.data?.detail || 'Failed to load ponds for this location');
    }
  };

  const handleCreatePond = async (data: PondFormData) => {
    if (!selectedLocationForPonds) return;
    
    try {
      const newPond = await createPond('/ponds', {
        ...data,
        location_id: selectedLocationForPonds.id,
      });
      if (newPond) {
        setPonds([...ponds, newPond]);
        setShowPondForm(false);
        pondForm.reset();
        // Update location pond count
        refetch();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create pond');
    }
  };

  const handleEditPond = (pond: Pond) => {
    setEditingPond(pond);
    pondForm.reset({
      name: pond.name,
      pond_type: pond.pond_type,
      size_sqm: pond.size_sqm || 0,
    });
    setShowPondForm(true);
  };

  const handleUpdatePond = async (data: PondFormData) => {
    if (!editingPond) return;
    
    try {
      await updatePond(`/ponds/${editingPond.id}`, {
        ...data,
        pond_id: editingPond.id,
      }, 'PUT');
      
      setPonds(ponds.map(pond => 
        pond.id === editingPond.id ? { ...pond, ...data } : pond
      ));
      setShowPondForm(false);
      setEditingPond(null);
      pondForm.reset();
    } catch (err: any) {
      setError(err.message || 'Failed to update pond');
    }
  };

  const handleDeletePond = async (pondId: string) => {
    if (!window.confirm('Are you sure you want to delete this pond?')) {
      return;
    }

    try {
      await deletePond(`/ponds/${pondId}`, {}, 'DELETE');
      setPonds(ponds.filter(pond => pond.id !== pondId));
      // Update location pond count
      refetch();
    } catch (err: any) {
      setError(err.message || 'Failed to delete pond');
    }
  };

  if (loading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Location Management</h1>
          <p className="text-gray-600 mt-1">Manage farm locations and facilities</p>
          {freeLocationLimitReached && (
            <p className="text-xs text-amber-700 mt-2">
              Free plan limit reached (1 farm). Upgrade to PRO to add more locations.
            </p>
          )}
        </div>
        {!freeLocationLimitReached && (
          <button
            onClick={() => {
              setEditingLocation(null);
              form.reset();
              setShowCreateForm(true);
            }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Location
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {editingLocation ? 'Edit Location' : 'Create New Location'}
          </h2>
          
          <form 
            onSubmit={form.handleSubmit(editingLocation ? handleUpdateLocation : handleCreateLocation)}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location Name *
                </label>
                <input
                  type="text"
                  {...form.register('name')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Main Fish Farm"
                />
                {form.formState.errors.name && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person
                </label>
                <input
                  type="text"
                  {...form.register('contact_person')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Farm Manager Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <input
                  type="text"
                  {...form.register('address')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Farm Address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  {...form.register('contact_phone')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Phone Number"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                {...form.register('description')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Location description and notes"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Types *
              </label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.watch('business_types')?.includes('fish') || false}
                    onChange={(e) => {
                      const current = form.getValues('business_types') || [];
                      if (e.target.checked) {
                        form.setValue('business_types', [...current, 'fish']);
                      } else {
                        const filtered = current.filter(t => t !== 'fish');
                        form.setValue('business_types', filtered.length > 0 ? filtered : ['fish']);
                      }
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">🐟 Fish Farming</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.watch('business_types')?.includes('goat') || false}
                    onChange={(e) => {
                      const current = form.getValues('business_types') || [];
                      if (e.target.checked) {
                        form.setValue('business_types', [...current, 'goat']);
                      } else {
                        form.setValue('business_types', current.filter(t => t !== 'goat'));
                      }
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">🐐 Goat Farming</span>
                </label>
              </div>
              {form.formState.errors.business_types && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.business_types.message}</p>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingLocation(null);
                  form.reset();
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
              >
                {creating ? <LoadingSpinner size="sm" /> : (editingLocation ? 'Update Location' : 'Create Location')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Locations List */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Existing Locations ({locations?.length || 0})
        </h2>

        {locations && locations.length > 0 ? (
          locations.map((location) => {
            const isDemoLocation = location.id === DEMO_LOCATION_ID;
            return (
            <div key={location.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start space-x-3 sm:space-x-4 min-w-0">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 break-words">{location.name}</h3>
                    
                    {location.address && (
                      <div className="flex items-center space-x-2 mt-2">
                        <MapPinIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{location.address}</span>
                      </div>
                    )}
                    
                    {location.contact_person && (
                      <div className="flex items-center space-x-2 mt-1">
                        <UserIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{location.contact_person}</span>
                      </div>
                    )}
                    
                    {location.contact_phone && (
                      <div className="flex items-center space-x-2 mt-1">
                        <PhoneIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{location.contact_phone}</span>
                      </div>
                    )}
                    
                    {location.description && (
                      <p className="text-sm text-gray-600 mt-2">{location.description}</p>
                    )}
                    
                    <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                      <span>🏊‍♂️ {location.pond_count} Ponds</span>
                      <span>Created: {new Date(location.created_at).toLocaleDateString()}</span>
                      <span className={`px-2 py-1 rounded-full ${location.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {location.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    {location.business_types && location.business_types.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {location.business_types.map((type) => (
                          <span
                            key={type}
                            className={`px-2 py-1 text-xs rounded-full ${
                              type === 'fish' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {type === 'fish' ? '🐟 Fish' : '🐐 Goat'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center flex-wrap gap-2 sm:gap-2 sm:justify-end">
                  <button
                    onClick={() => handleManagePonds(location)}
                    className="px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    🏊‍♂️ Manage Ponds
                  </button>
                  <button
                    onClick={() => handleEditLocation(location)}
                    className="p-2 rounded-md border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-200"
                    aria-label="Edit location"
                    title="Edit location"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  {!isDemoLocation && (
                    <button
                      onClick={() => handleDeleteLocation(location.id)}
                      className="p-2 rounded-md border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200"
                      aria-label="Delete location"
                      title="Delete location"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            );
          })
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <BuildingOfficeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Locations Created</h3>
            <p className="text-gray-600 mb-4">Create your first farm location to get started.</p>
            <button
              onClick={() => {
                setEditingLocation(null);
                form.reset();
                setShowCreateForm(true);
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create First Location
            </button>
          </div>
        )}
      </div>

      {/* Pond Management Modal */}
      {showPondManagement && selectedLocationForPonds && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Manage Ponds - {selectedLocationForPonds.name}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Add, edit, or remove ponds for this location
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPondManagement(false);
                  setSelectedLocationForPonds(null);
                  setPonds([]);
                  setShowPondForm(false);
                  setEditingPond(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Add Pond Button */}
              <div className="mb-6">
                <button
                  onClick={() => {
                    setEditingPond(null);
                    pondForm.reset();
                    setShowPondForm(true);
                  }}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add New Pond
                </button>
              </div>

              {/* Pond Form */}
              {showPondForm && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {editingPond ? 'Edit Pond' : 'Add New Pond'}
                  </h3>
                  
                  <form 
                    onSubmit={pondForm.handleSubmit(editingPond ? handleUpdatePond : handleCreatePond)}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pond Name *
                        </label>
                        <input
                          type="text"
                          {...pondForm.register('name')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., S1, G1, Pond A"
                        />
                        {pondForm.formState.errors.name && (
                          <p className="mt-1 text-sm text-red-600">{pondForm.formState.errors.name.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pond Type *
                        </label>
                        <select
                          {...pondForm.register('pond_type')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="nursery">Nursery</option>
                          <option value="grower">Grower</option>
                        </select>
                        {pondForm.formState.errors.pond_type && (
                          <p className="mt-1 text-sm text-red-600">{pondForm.formState.errors.pond_type.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pond Area (sqm) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          {...pondForm.register('size_sqm', { valueAsNumber: true })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., 1000, 5000"
                        />
                        {pondForm.formState.errors.size_sqm && (
                          <p className="mt-1 text-sm text-red-600">{pondForm.formState.errors.size_sqm.message}</p>
                        )}
                      </div>

                      <div className="text-sm text-gray-500 flex items-end pb-2">
                        <div>
                          <p><strong>Typical sizes:</strong></p>
                          <p>• Nursery: 100-1000 sqm</p>
                          <p>• Grower: 1000-10000 sqm</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowPondForm(false);
                          setEditingPond(null);
                          pondForm.reset({
                            name: '',
                            pond_type: 'nursery',
                            size_sqm: 0,
                          });
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={creatingPond}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
                      >
                        {creatingPond ? <LoadingSpinner size="sm" /> : (editingPond ? 'Update Pond' : 'Create Pond')}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Ponds List */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Ponds ({ponds.length})
                </h3>

                {ponds.length > 0 ? (
                  ponds.map((pond) => (
                    <div key={pond.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${
                            pond.pond_type === 'nursery' ? 'bg-blue-100' : 'bg-green-100'
                          }`}>
                            <span className="text-lg">
                              {pond.pond_type === 'nursery' ? '🐟' : '🐠'}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{pond.name}</h4>
                            <p className="text-sm text-gray-600 capitalize">
                              {pond.pond_type} Pond
                              {pond.size_sqm && ` • ${pond.size_sqm.toLocaleString()} sqm`}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditPond(pond)}
                            className="p-2 text-gray-400 hover:text-blue-600"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePond(pond.id)}
                            className="p-2 text-gray-400 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">🏊‍♂️</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Ponds Yet</h3>
                    <p className="text-gray-600 mb-4">Add your first pond to get started with data entry.</p>
                    <button
                      onClick={() => {
                        setEditingPond(null);
                        pondForm.reset();
                        setShowPondForm(true);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Add First Pond
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationManagement;